import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Badge,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { ticketsAPI } from "../../services/api"
import withRouter from "components/Common/withRouter"
import MentionInput from "../../components/MentionInput"
import { CommentWithMentions } from "../../utils/mentionUtils"

const TicketDetails = (props) => {
    const { id } = props.router.params
    const [ticket, setTicket] = useState(null)
    const [newComment, setNewComment] = useState("")
    const [commentFiles, setCommentFiles] = useState([])
    const [isUploading, setIsUploading] = useState(false)

    useEffect(() => {
        fetchTicket()
    }, [id])

    const fetchTicket = async () => {
        try {
            const res = await ticketsAPI.getById(id)
            setTicket(res.data)
            props.setBreadcrumbItems(`Ticket #${res.data.id}`, [
                { title: "Dashboard", link: "/dashboard" },
                { title: "Tickets", link: "/tickets" },
                { title: `Ticket #${res.data.id}`, link: "#" },
            ])
        } catch (error) {
            console.error("Erreur lors du chargement du ticket", error)
        }
    }

    const handleStatusChange = async (statut) => {
        try {
            await ticketsAPI.changeStatus(id, statut)
            fetchTicket()
        } catch (error) {
            console.error("Erreur lors du changement de statut", error)
        }
    }

    const handleAddComment = async (e) => {
        e.preventDefault()
        if (!newComment.trim() && commentFiles.length === 0) return // Allow comment with only files

        setIsUploading(true)
        try {
            const formData = new FormData()
            if (newComment.trim()) {
                formData.append("commentaire", newComment)
            }

            // Ajouter les fichiers s'il y en a
            commentFiles.forEach((file, index) => {
                formData.append(`fichiers[${index}]`, file)
            })

            await ticketsAPI.addCommentaire(id, formData)
            setNewComment("")
            setCommentFiles([])
            fetchTicket()
        } catch (error) {
            console.error("Erreur lors de l'ajout du commentaire", error)
        } finally {
            setIsUploading(false)
        }
    }

    const handleCommentFileSelect = (e) => {
        const files = Array.from(e.target.files)
        setCommentFiles(prev => [...prev, ...files])
        e.target.value = null; // Reset the input so the same file can be selected again
    }

    const handleRemoveCommentFile = (index) => {
        setCommentFiles(prev => prev.filter((_, i) => i !== index))
    }

    const handleFileUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        const formData = new FormData()
        formData.append("fichier", file)

        setIsUploading(true)
        try {
            await ticketsAPI.addAttachment(id, formData)
            fetchTicket()
        } catch (error) {
            console.error("Erreur lors de l'upload", error)
        } finally {
            setIsUploading(false)
        }
    }

    const handleDeleteAttachment = async (attachmentId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette pièce jointe ?")) {
            return
        }

        try {
            await ticketsAPI.deleteAttachment(id, attachmentId)
            fetchTicket()
        } catch (error) {
            console.error("Erreur lors de la suppression", error)
            alert("Erreur lors de la suppression de la pièce jointe")
        }
    }

    if (!ticket) return <div>Chargement...</div>

    const getPriorityBadge = (prio) => {
        const colors = { basse: 'info', normale: 'secondary', haute: 'primary', urgente: 'danger' }
        return <Badge color={colors[prio] || 'secondary'} className="p-2">{prio.toUpperCase()}</Badge>
    }

    const getStatusBadge = (statut) => {
        const colors = { en_cours: 'warning', en_attente: 'info', reopen: 'warning', resolu: 'success', ferme: 'secondary' }
        return <Badge color={colors[statut] || 'secondary'} className="p-2">{statut.toUpperCase().replace('_', ' ')}</Badge>
    }

    return (
        <React.Fragment>
            <Row>
                <Col lg="8">
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center mb-4">
                                <div className="flex-grow-1">
                                    <h4 className="card-title mb-1">{ticket.titre}</h4>
                                    <p className="text-muted mb-0">
                                        Projet : <Badge color="light" className="text-primary">{ticket.projet?.nom}</Badge>
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    {getStatusBadge(ticket.statut)}
                                    {getPriorityBadge(ticket.priorite)}
                                </div>
                            </div>

                            <div className="mb-4">
                                <h5 className="font-size-15 border-bottom pb-2">Description</h5>
                                <div className="p-3 bg-light rounded text-muted">
                                    {ticket.description || "Aucune description fournie."}
                                </div>
                            </div>

                            <div className="mt-5">
                                <h5 className="font-size-15 border-bottom pb-2 mb-4">Discussion ({ticket.commentaires?.length || 0})</h5>
                                <div className="comments-list mb-4" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    {ticket.commentaires?.map(comment => (
                                        <div key={comment.id} className="mb-4 d-flex">
                                            <div className="flex-shrink-0 me-3">
                                                <div className="avatar-xs">
                                                    <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                                        {comment.user?.name.charAt(0)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <h6 className="font-size-14 mb-0 fw-bold">{comment.user?.name}</h6>
                                                    <small className="text-muted">{new Date(comment.created_at).toLocaleString()}</small>
                                                </div>
                                                {comment.commentaire && (
                                                    <div className="bg-soft-light p-2 rounded mb-2">
                                                        <CommentWithMentions text={comment.commentaire} className="text-muted mb-0" />
                                                    </div>
                                                )}
                                                {comment.attachments && comment.attachments.length > 0 && (
                                                    <div className="attachments-in-comment mt-2">
                                                        {comment.attachments.map(file => (
                                                            <div key={file.id} className="border rounded p-2 mb-2 d-flex align-items-center bg-white">
                                                                <div className="avatar-xs me-2">
                                                                    <span className="avatar-title bg-soft-primary text-primary rounded font-size-10">
                                                                        {file.fichier_nom_original.split('.').pop().toUpperCase()}
                                                                    </span>
                                                                </div>
                                                                <div className="flex-grow-1 overflow-hidden">
                                                                    <p className="text-truncate mb-0 font-size-13 text-dark fw-bold">{file.fichier_nom_original}</p>
                                                                    <small className="text-muted">{(file.taille / 1024).toFixed(1)} KB</small>
                                                                </div>
                                                                <div className="d-flex gap-1">
                                                                    <a href={file.url} target="_blank" rel="noreferrer" className="btn btn-link link-success p-1" title="Télécharger">
                                                                        <i className="mdi mdi-download h5 mb-0"></i>
                                                                    </a>
                                                                    <button
                                                                        onClick={() => handleDeleteAttachment(file.id)}
                                                                        className="btn btn-link link-danger p-1"
                                                                        title="Supprimer"
                                                                    >
                                                                        <i className="mdi mdi-delete h5 mb-0"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {(!ticket.commentaires || ticket.commentaires.length === 0) && (
                                        <div className="text-center py-4 border rounded dashed">
                                            <i className="mdi mdi-message-outline h2 text-muted"></i>
                                            <p className="text-muted">Aucun commentaire pour le moment</p>
                                        </div>
                                    )}
                                </div>

                                <Form onSubmit={handleAddComment}>
                                    <FormGroup className="mb-0">
                                        <MentionInput
                                            value={newComment}
                                            onChange={setNewComment}
                                            onSubmit={handleAddComment}
                                            ticketId={id}
                                            placeholder="Saisissez un commentaire... (utilisez @ pour mentionner)"
                                        />

                                        {/* Display selected files */}
                                        {commentFiles.length > 0 && (
                                            <div className="mt-2 p-2 border rounded bg-light">
                                                <small className="text-muted d-block mb-2">Fichiers à joindre (<Button className="icons-xs me-1">{commentFiles.length}</Button>) :</small>
                                                {commentFiles.map((file, index) => (
                                                    <div key={index} className="d-flex align-items-center justify-content-between mb-1 p-1 bg-white rounded">
                                                        <div className="d-flex align-items-center">
                                                            <i className="mdi mdi-file-outline me-2 text-primary"></i>
                                                            <small className="text-truncate" style={{ maxWidth: '200px' }}>{file.name}</small>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveCommentFile(index)}
                                                            className="btn btn-sm btn-link text-danger p-0"
                                                        >
                                                            <i className="mdi mdi-close"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                            <div>
                                                <input
                                                    type="file"
                                                    id="comment-file-upload"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    onChange={handleCommentFileSelect}
                                                />
                                                <Button
                                                    type="button"
                                                    color="soft-secondary"
                                                    size="sm"
                                                    onClick={() => document.getElementById('comment-file-upload').click()}
                                                >
                                                    <i className="mdi mdi-paperclip me-1"></i> Joindre fichier
                                                </Button>
                                            </div>
                                            <Button
                                                color="primary"
                                                type="submit"
                                                disabled={!newComment.trim() && commentFiles.length === 0}
                                            >
                                                {isUploading ? (
                                                    <>
                                                        <i className="mdi mdi-loading mdi-spin me-1"></i> Envoi...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="mdi mdi-send me-1"></i> Répondre
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </FormGroup>
                                </Form>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="4">
                    <Card>
                        <CardBody>
                            <CardTitle className="h5 mb-4 border-bottom pb-2">Actions Rapides</CardTitle>

                            <div className="mb-4">
                                <Label className="small text-uppercase text-muted fw-bold">Statut du Ticket</Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {['en_cours', 'en_attente', 'reopen', 'resolu', 'ferme'].map(s => {
                                        const user = JSON.parse(localStorage.getItem('user') || '{}');
                                        const isCollaborateur = user.role === 'collaborateur';

                                        // Un collaborateur ne peut pas fermer un ticket
                                        if (isCollaborateur && s === 'ferme') {
                                            return null;
                                        }

                                        return (
                                            <Button
                                                key={s}
                                                color={ticket.statut === s ? "primary" : "outline-light"}
                                                size="sm"
                                                onClick={() => handleStatusChange(s)}
                                                className={ticket.statut === s ? "" : "text-dark"}
                                            >
                                                {s.replace('_', ' ')}
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>

                            <hr />

                            <div className="mt-4">
                                <table className="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr><th className="ps-0">Assigné à</th><td className="text-end text-primary fw-bold">{ticket.assigned_to?.name || 'Non assigné'}</td></tr>
                                        <tr><th className="ps-0">Responsable</th><td className="text-end">{ticket.created_by?.name || '-'}</td></tr>
                                        <tr><th className="ps-0">Deadline</th><td className="text-end text-danger">{ticket.deadline ? new Date(ticket.deadline).toLocaleDateString() : '-'}</td></tr>
                                        <tr><th className="ps-0">Estimation</th><td className="text-end text-success">{ticket.heures_estimees || 0}h</td></tr>
                                        <tr><th className="ps-0">Temps passé</th><td className="text-end text-info">{ticket.heures_reelles || 0}h</td></tr>
                                    </tbody>
                                </table>
                            </div>

                            <hr />

                            <div className="mt-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="font-size-15 mb-0">
                                        Pièces Jointes du Ticket
                                        <small className="text-muted font-size-11 ms-2">(Au niveau du ticket)</small>
                                    </h5>
                                    <div className="position-relative">
                                        <input
                                            type="file"
                                            id="attachment-file"
                                            style={{ display: "none" }}
                                            onChange={handleFileUpload}
                                        />
                                        <Button
                                            color="soft-primary"
                                            size="sm"
                                            onClick={() => document.getElementById('attachment-file').click()}
                                            disabled={isUploading}
                                        >
                                            {isUploading ? <i className="mdi mdi-loading mdi-spin"></i> : <i className="mdi mdi-plus"></i>}
                                        </Button>
                                    </div>
                                </div>
                                <div className="attachments-list">
                                    {ticket.attachments?.filter(file => !file.commentaire_id).map(file => (
                                        <div key={file.id} className="border rounded p-2 mb-2 d-flex align-items-center bg-light">
                                            <div className="avatar-xs me-2">
                                                <span className="avatar-title bg-primary rounded text-white font-size-10">
                                                    {file.fichier_nom_original.split('.').pop().toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-grow-1 overflow-hidden">
                                                <p className="text-truncate mb-0 font-size-13 text-dark fw-bold">{file.fichier_nom_original}</p>
                                                <small className="text-muted">{(file.taille / 1024).toFixed(1)} KB • {file.user?.name}</small>
                                            </div>
                                            <div className="d-flex gap-1">
                                                <a href={file.url} target="_blank" rel="noreferrer" className="btn btn-link link-success p-1" title="Télécharger">
                                                    <i className="mdi mdi-download h4 mb-0"></i>
                                                </a>
                                                <button
                                                    onClick={() => handleDeleteAttachment(file.id)}
                                                    className="btn btn-link link-danger p-1"
                                                    title="Supprimer"
                                                >
                                                    <i className="mdi mdi-delete h4 mb-0"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {(!ticket.attachments || ticket.attachments.filter(f => !f.commentaire_id).length === 0) && (
                                        <div className="text-center py-3 border rounded bg-light">
                                            <i className="mdi mdi-information-outline h3 text-muted"></i>
                                            <p className="text-muted small mb-1">Aucun fichier joint au niveau du ticket.</p>
                                            <small className="text-muted font-size-11">Les fichiers joints aux commentaires apparaissent dans la discussion.</small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(withRouter(TicketDetails))
